import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, CheckCircle, LogIn, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

function getSlackInstallUrl() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL + '/api/slack/install';
  }
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3000') {
      return 'http://localhost:5001/api/slack/install';
    }
    return window.location.origin + '/api/slack/install';
  }
  return '/api/slack/install';
}

export default function SlackInstall() {
  const { isAuthenticated, loading } = useAuth();
  const { isDark } = useTheme();
  const slackInstallUrl = getSlackInstallUrl();

  const bg = isDark ? 'bg-zinc-950' : 'bg-gray-50';
  const card = isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200';
  const text = isDark ? 'text-white' : 'text-gray-900';
  const muted = isDark ? 'text-zinc-400' : 'text-gray-600';
  const accent = isDark ? 'text-cyan-400' : 'text-cyan-600';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bg}`}>
        <div className="animate-pulse text-lg text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg}`}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-cyan-500/20 mb-4">
            <MessageCircle className="h-7 w-7 text-cyan-500" />
          </div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${text} mb-2`}>
            Add ShowStop to Slack
          </h1>
          <p className={`text-base ${muted}`}>
            Get new trend notifications in your Slack channel
          </p>
        </div>

        {/* What you get */}
        <div className={`${card} rounded-2xl border p-6 sm:p-8 mb-8`}>
          <h2 className={`text-sm font-semibold uppercase tracking-wider ${muted} mb-4`}>
            What you get
          </h2>
          <ul className={`space-y-3 text-sm ${text}`}>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
              <span>When a new trend is added in ShowStop Trend Spotter, a message is posted to your chosen Slack channel.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
              <span>Each message includes the trend name, type, video count, why it works, hook details, and links to top videos.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
              <span>You choose the channel once during setup; no manual webhook configuration.</span>
            </li>
          </ul>
        </div>

        {/* Steps */}
        <div className={`${card} rounded-2xl border p-6 sm:p-8 mb-8`}>
          <h2 className={`text-sm font-semibold uppercase tracking-wider ${muted} mb-6`}>
            How to connect
          </h2>
          <ol className="space-y-6">
            <li className="flex gap-4">
              <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                1
              </span>
              <div>
                <h3 className={`font-medium ${text} mb-1`}>
                  {isAuthenticated ? "You're logged in" : 'Log in to ShowStop'}
                </h3>
                <p className={`text-sm ${muted} mb-3`}>
                  {isAuthenticated
                    ? 'Your ShowStop account is connected. You can proceed to the next step.'
                    : 'You need to sign in to ShowStop first so we can attach Slack to your account.'}
                </p>
                {!isAuthenticated && (
                  <Link
                    to="/login"
                    state={{ from: { pathname: '/slack-install' } }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-cyan-500 hover:bg-cyan-600 text-white'}`}
                  >
                    <LogIn className="h-4 w-4" />
                    Log in to ShowStop
                  </Link>
                )}
              </div>
            </li>
            <li className="flex gap-4">
              <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                2
              </span>
              <div>
                <h3 className={`font-medium ${text} mb-1`}>Click Add to Slack</h3>
                <p className={`text-sm ${muted} mb-3`}>
                  You'll be taken to Slack to authorize the app and pick the channel where you want notifications.
                </p>
                {isAuthenticated ? (
                  <a
                    href={slackInstallUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = slackInstallUrl;
                    }}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: '#4A154B' }}
                  >
                    <img src="/slack-logo.png" alt="" className="h-5 w-5" />
                    Add to Slack
                  </a>
                ) : (
                  <p className={`text-sm ${muted} italic`}>Log in above first, then this button will appear.</p>
                )}
              </div>
            </li>
            <li className="flex gap-4">
              <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                3
              </span>
              <div>
                <h3 className={`font-medium ${text} mb-1`}>Choose your channel</h3>
                <p className={`text-sm ${muted}`}>
                  In Slack, select the channel (or create one) where you want new trend alerts. Click Allow.
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isDark ? 'bg-zinc-700 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                4
              </span>
              <div>
                <h3 className={`font-medium ${text} mb-1`}>Done</h3>
                <p className={`text-sm ${muted}`}>
                  New trends added in Trend Spotter will be posted to that channel. You can disconnect or change the channel later from your account.
                </p>
              </div>
            </li>
          </ol>
        </div>

        {/* Back to app */}
        <div className="text-center">
          <Link
            to="/trend-spotter"
            className={`inline-flex items-center gap-2 text-sm font-medium ${accent} hover:underline`}
          >
            <ExternalLink className="h-4 w-4" />
            Back to Trend Spotter
          </Link>
        </div>
      </div>
    </div>
  );
}
